import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CloseOutlined, OndemandVideoOutlined } from '@mui/icons-material';
import { slugify } from '../constants';
import { InspSectionTitle, InspRow, InspInput, ToggleRow, selectStyle } from '../ui/primitives';
import { MediaField } from '../MediaField';

const PLENARY_COLOR = '#8b4fcc';

/**
 * Left panel shown when a plenary page is active.
 * Clicking on the canvas background selects the whole page (selectedId = 'canvas').
 * Clicking on the video/chat/session widgets sets selectedId to that component id.
 * This inspector shows settings for whichever is selected.
 */
export function PlenaryInspector({ plenary, selectedComponent, onUpdate, onClose }) {
  if (!plenary) return null;
  const upd = patch => onUpdate(patch);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.75, borderBottom: '1px solid #e8edf2',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 26, height: 26, borderRadius: 1.5,
            background: alpha(PLENARY_COLOR, 0.1),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: PLENARY_COLOR,
          }}>
            <OndemandVideoOutlined sx={{ fontSize: 15 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a2e', lineHeight: 1 }}>
              {COMPONENT_LABELS[selectedComponent] || 'Plenary Settings'}
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: '#b0bac8', mt: 0.2 }}>
              {plenary.name}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#9ea8b3' }}>
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {/* Component selector tabs */}
        <ComponentTabs selected={selectedComponent} />

        {selectedComponent === 'page'    && <PageSettings    plenary={plenary} upd={upd} />}
        {selectedComponent === 'video'   && <VideoSettings   plenary={plenary} upd={upd} />}
        {selectedComponent === 'chat'    && <ChatSettings    plenary={plenary} upd={upd} />}
        {selectedComponent === 'session' && <SessionSettings plenary={plenary} upd={upd} />}
      </Box>
    </Box>
  );
}

// ── Component labels ──────────────────────────────────────────────────────────
const COMPONENT_LABELS = {
  page:    'Page Settings',
  video:   'Video Player',
  chat:    'Chat Panel',
  session: 'Session Info',
};

// Small tab strip so user can jump between sections without clicking canvas
function ComponentTabs({ selected }) {
  // Read-only indicator — clicking on canvas components sets the active one.
  // This just shows which component's settings are visible.
  const tabs = [
    { id: 'page',    label: 'Page'    },
    { id: 'video',   label: 'Video'   },
    { id: 'chat',    label: 'Chat'    },
    { id: 'session', label: 'Session' },
  ];
  return (
    <Box sx={{ display: 'flex', gap: '3px', mb: 2, p: '3px', background: '#f0f4f8', borderRadius: '8px' }}>
      {tabs.map(t => (
        <Box
          key={t.id}
          sx={{
            flex: 1, textAlign: 'center', py: 0.6,
            borderRadius: '6px', fontSize: '0.65rem', fontWeight: 700,
            cursor: 'default', userSelect: 'none',
            background: selected === t.id ? '#fff' : 'transparent',
            color: selected === t.id ? PLENARY_COLOR : '#9ea8b3',
            boxShadow: selected === t.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.12s',
          }}
        >
          {t.label}
        </Box>
      ))}
    </Box>
  );
}

// ── Page-level settings ───────────────────────────────────────────────────────
function PageSettings({ plenary, upd }) {
  return (
    <>
      <InspSectionTitle label="General" color={PLENARY_COLOR} />

      <InspRow label="Name">
        <InspInput value={plenary.name || ''} onChange={v => upd({ name: v })} placeholder="e.g. Main Stage" />
      </InspRow>

      <InspRow label="Slug">
        <InspInput value={plenary.slug || ''} onChange={v => upd({ slug: slugify(v) })} placeholder="main-stage" />
      </InspRow>

      <ToggleRow label="Default plenary"  value={!!plenary.isDefault}        onChange={v => upd({ isDefault: v })} />
      <ToggleRow label="Active"           value={plenary.isActive !== false}  onChange={v => upd({ isActive: v })} />

      <InspSectionTitle label="Background" color="#e67e22" />

      <InspRow label="Type">
        <select value={plenary.bgType || 'color'} onChange={e => upd({ bgType: e.target.value })} style={selectStyle}>
          <option value="color">Solid color</option>
          <option value="image">Image</option>
          <option value="video">Video</option>
        </select>
      </InspRow>

      {plenary.bgType === 'color' || !plenary.bgType ? (
        <InspRow label="Color">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="color"
              value={plenary.bgColor || '#0D1B2A'}
              onChange={e => upd({ bgColor: e.target.value })}
              style={{ width: 28, height: 28, border: '1px solid #e4e9f0', borderRadius: 4, padding: 2, cursor: 'pointer', background: 'none' }}
            />
            <InspInput value={plenary.bgColor || '#0D1B2A'} onChange={v => upd({ bgColor: v })} placeholder="#0D1B2A" />
          </Box>
        </InspRow>
      ) : (
        <InspRow label={plenary.bgType === 'video' ? 'Video' : 'Image'}>
          <MediaField
            value={plenary.bgUrl || ''}
            onChange={v => upd({ bgUrl: v })}
            mediaType={plenary.bgType === 'video' ? 'video' : 'image'}
          />
        </InspRow>
      )}

      <InspSectionTitle label="Offline State" color="#9ea8b3" />

      <InspRow label="Offline Title">
        <InspInput
          value={plenary.offlineTitle || ''}
          onChange={v => upd({ offlineTitle: v })}
          placeholder="Session Starting Soon"
        />
      </InspRow>

      <InspRow label="Offline Message">
        <textarea
          value={plenary.offlineMessage || ''}
          onChange={e => upd({ offlineMessage: e.target.value })}
          placeholder="Please check the schedule…"
          rows={3}
          style={{
            width: '100%', border: '1px solid #e4e9f0', borderRadius: 6,
            padding: '6px 10px', fontSize: '12px', outline: 'none',
            fontFamily: 'inherit', background: '#f8f9fb', boxSizing: 'border-box',
            resize: 'vertical',
          }}
        />
      </InspRow>

      <InspRow label="Offline BG Image URL">
        <InspInput value={plenary.offlineBgUrl || ''} onChange={v => upd({ offlineBgUrl: v })} placeholder="https://…" />
      </InspRow>
    </>
  );
}

// ── Video player settings ─────────────────────────────────────────────────────
function VideoSettings({ plenary, upd }) {
  return (
    <>
      <InspSectionTitle label="Live Stream" color="#EF4444" />

      <InspRow label="Stream Type">
        <select value={plenary.streamType || 'hls'} onChange={e => upd({ streamType: e.target.value })} style={selectStyle}>
          <option value="hls">HLS (.m3u8)</option>
          <option value="iframe">Embed (iframe)</option>
          <option value="youtube">YouTube</option>
          <option value="vimeo">Vimeo</option>
        </select>
      </InspRow>

      <InspRow label="Stream URL">
        <InspInput
          value={plenary.streamUrl || ''}
          onChange={v => upd({ streamUrl: v })}
          placeholder={
            plenary.streamType === 'hls'     ? 'https://…/stream.m3u8' :
            plenary.streamType === 'youtube' ? 'https://www.youtube.com/embed/…' :
            plenary.streamType === 'vimeo'   ? 'https://player.vimeo.com/video/…' :
            'https://…'
          }
        />
      </InspRow>

      <InspSectionTitle label="Viewer Count" color="#9ea8b3" />

      <InspRow label="Viewer Count">
        <InspInput
          type="number"
          value={plenary.viewerCount ?? 0}
          onChange={v => upd({ viewerCount: parseInt(v, 10) || 0 })}
          placeholder="0"
        />
      </InspRow>
      <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', mb: 1.5, lineHeight: 1.5 }}>
        Set manually here. In production this is updated by your streaming infrastructure.
      </Typography>
    </>
  );
}

// ── Chat panel settings ───────────────────────────────────────────────────────
function ChatSettings({ plenary, upd }) {
  return (
    <>
      <InspSectionTitle label="Chat" color="#22c55e" />

      <ToggleRow label="Enable chat panel" value={plenary.chatEnabled !== false} onChange={v => upd({ chatEnabled: v })} />

      <InspRow label="Panel Title">
        <InspInput
          value={plenary.chatTitle || ''}
          onChange={v => upd({ chatTitle: v })}
          placeholder="Live Discussion"
        />
      </InspRow>

      <InspSectionTitle label="External Embed" color="#9ea8b3" />
      <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', mb: 1, lineHeight: 1.5 }}>
        Leave blank to use the built-in chat. Paste a Slido, StreamYard or other embed URL to override it.
      </Typography>

      <InspRow label="Chat Embed URL">
        <InspInput
          value={plenary.chatEmbedUrl || ''}
          onChange={v => upd({ chatEmbedUrl: v || null })}
          placeholder="https://app.sli.do/event/…"
        />
      </InspRow>
    </>
  );
}

// ── Session info settings ─────────────────────────────────────────────────────
function SessionSettings({ plenary, upd }) {
  const sessionIds = (plenary.sessionIds || []).join(', ');

  return (
    <>
      <InspSectionTitle label="Session Filtering" color="#1075BA" />
      <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', mb: 1.5, lineHeight: 1.5 }}>
        By default, sessions whose <strong>location</strong> matches this stage's name are shown.
        To pin specific sessions instead, list their IDs below.
      </Typography>

      <InspRow label="Session IDs (comma-separated)">
        <InspInput
          value={sessionIds}
          onChange={v => upd({
            sessionIds: v
              .split(',')
              .map(s => s.trim())
              .filter(Boolean),
          })}
          placeholder="session-01, session-02"
        />
      </InspRow>

      <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', mb: 2, lineHeight: 1.5 }}>
        IDs come from your event schedule items. Leave empty to match by stage name automatically.
      </Typography>

      <InspSectionTitle label="Stage Name (for auto-match)" color="#9ea8b3" />
      <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', mb: 1, lineHeight: 1.5 }}>
        Sessions with <code style={{ background: '#f0f4f8', padding: '1px 4px', borderRadius: 3 }}>location</code> equal
        to this name are shown when no session IDs are set.
      </Typography>
      <InspRow label="Stage Name">
        <InspInput value={plenary.name || ''} onChange={v => upd({ name: v })} placeholder="Main Stage" />
      </InspRow>
    </>
  );
}